import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { TABLE_RECORD_PER_PAGE } from "../../../constants";
import { apiEndpoints, httpMethods } from "../../../constants/api.constants";
import {
  loaderPath,
  musharakahMaterialsColumns,
  musharakahMaterialsFields,
} from "../../../descriptions/settings.descriptions";
import useAPI from "../../../hooks/useAPI";
import useDebounce from "../../../hooks/useDebounce";
import useI18N from "../../../hooks/useI18N";
import usePagination from "../../../hooks/usePagination";
import { addMusharakahMaterials } from "../../../redux/slices/settings.slices";
import { addRowsPerPage } from "../../../redux/slices/table.slices";
import { Edit, Search } from "../../../shared/icon";
import MUIModal from "../../../shared/MUIModal";
import { MUIStyled } from "../../../shared/MUIStyled";
import SABasicTable from "../../../shared/SABasicTable";
import SABox from "../../../shared/SABox";
import SAButton from "../../../shared/SAButton";
import SACard from "../../../shared/SACard";
import SAForm from "../../../shared/SAForm";
import SAFormFields from "../../../shared/SAFormFields";
import SAStack from "../../../shared/SAStack";
import SAStatusBadge from "../../../shared/SAStatusBadge";
import SATextField from "../../../shared/SATextField";
import SATypography from "../../../shared/SATypography";

const SearchWrapper = MUIStyled(SAStack)({
  maxWidth: 400,
  width: "100%",
});

const MusharakahMaterials = () => {
  const { t } = useI18N();
  const { api } = useAPI();
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [modalDefaultValues, setModalDefaultValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [preciousItemAttributes, setPreciousItemAttributes] = useState(null);

  const { register, handleSubmit, reset, control, formState } = useForm({
    defaultValues: modalDefaultValues,
    mode: "onChange",
  });
  const { isDirty } = formState;

  const materialsList = useSelector(
    (state) => state.settings?.musharakahMaterialsList,
  );
  const { count, results: materials } = materialsList || {};

  const tableRows = useSelector(
    (state) => state?.rowsPerPage?.musharakahMaterials,
  );

  const debouncedSearch = useDebounce(searchTerm, 300, () => setCurrentPage(1));

  const {
    totalPage,
    currentPage,
    itemPerPage,
    setCurrentPage,
    setItemPerPage,
  } = usePagination(count, tableRows || TABLE_RECORD_PER_PAGE);

  const handleSetItemPerPage = (val) => {
    setItemPerPage(val);
    dispatch(addRowsPerPage({ name: "musharakahMaterials", data: val }));
  };

  useEffect(() => {
    fetchMaterials();
  }, [currentPage, itemPerPage, debouncedSearch]);

  useEffect(() => {
    if (modalOpen) {
      if (!preciousItemAttributes) {
        fetchAttributes();
      } else {
        const values = { ...modalDefaultValues };
        if (values.carat_type) {
          const matchedCarat = preciousItemAttributes.metal_carat_types?.find(
            (item) =>
              item.id === values.carat_type || item.name === values.carat_type,
          );
          if (matchedCarat) values.carat_type = matchedCarat.id;
        }
        reset(values);
      }
    }
  }, [modalOpen, modalDefaultValues, reset, preciousItemAttributes]);

  const fetchMaterials = async () => {
    setLoading(true);
    const response = await api({
      method: httpMethods.get,
      endPoint: apiEndpoints.musharakahContractMaterial,
      loader: loaderPath.getMusharakahMaterial,
      needLoader: true,
      params: {
        page: currentPage,
        page_size: itemPerPage,
        name: debouncedSearch,
      },
      showToastOnError: true,
    });
    if (response?.status) {
      dispatch(addMusharakahMaterials({ data: response?.data }));
    }
    setLoading(false);
  };

  const fetchAttributes = async () => {
    const response = await api({
      method: httpMethods.get,
      endPoint: apiEndpoints.preciousItemAttributes,
      showToastOnError: true,
    });
    if (response?.status) {
      setPreciousItemAttributes(response?.data);
    }
  };

  const handleStatusChange = async (data) => {
    if (!data?.id) return;
    setRowLoading(data?.id);
    const response = await api({
      method: httpMethods.patch,
      endPoint: `${apiEndpoints.musharakahContractMaterial}${data.id}/`,
      data: { is_enabled: !data.is_enabled },
      showToastMessage: true,
    });
    if (response?.status) fetchMaterials();
    setRowLoading(null);
  };

  const openAddModal = () => {
    setModalMode("add");
    setModalDefaultValues({ is_active: true });
    setModalOpen(true);
  };

  const openEditModal = (data) => {
    setModalMode("edit");
    setModalDefaultValues({
      id: data.id,
      material_item: data.material_item,
      carat_type: data.carat_type,
      is_enabled: data.is_enabled,
    });
    setModalOpen(true);
  };

  const onSubmit = async (formData) => {
    setUpdateLoading(true);
    const isEdit = Boolean(formData.id);
    const response = await api({
      method: isEdit ? httpMethods.patch : httpMethods.post,
      endPoint: isEdit
        ? `${apiEndpoints.musharakahContractMaterial}${formData.id}/`
        : apiEndpoints.musharakahContractMaterial,
      data: formData,
      loader: loaderPath.addMusharakahMaterial,
      needLoader: true,
      showToastMessage: true,
    });
    if (response?.status) {
      fetchMaterials();
      setModalOpen(false);
    }
    setUpdateLoading(false);
  };

  const paginationProps = {
    currentPage,
    setItemPerPage: handleSetItemPerPage,
    setCurrentPage,
    page: totalPage,
    rowsPerPage: itemPerPage,
  };

  const renderCell = (columnKey, data) => {
    switch (columnKey) {
      case "material_item":
        return <SATypography variant="body2">{data?.name || "-"}</SATypography>;
      case "carat_type":
        return (
          <SATypography variant="body2">{data?.carat_type || "-"}</SATypography>
        );
      case "status":
        return (
          <SAStatusBadge status={data?.is_enabled ? "ENABLED" : "DISABLED"} />
        );
      case "action":
        return (
          <SAButton
            size="small"
            variant="outlined"
            onClick={() => handleStatusChange(data)}
            loading={rowLoading === data?.id}
          >
            {t(data?.is_enabled ? "disable" : "enable")}
          </SAButton>
        );
      case "edit":
        return (
          <SAButton
            size="small"
            variant="outlined"
            onClick={() => openEditModal(data)}
            sx={{ "& svg": { ml: 1 } }}
          >
            {t("edit-button")}
            <Edit />
          </SAButton>
        );
      default:
        return data[columnKey];
    }
  };

  const getOptions = (key) => {
    if (!preciousItemAttributes) return [];
    if (key === "material_item") {
      return (
        preciousItemAttributes.material_items
          ?.filter((item) => item.is_enabled)
          .map((item) => ({
            label: item.name,
            value: item.id,
          })) || []
      );
    }
    if (key === "carat_type") {
      return (
        preciousItemAttributes.metal_carat_types
          ?.filter((item) => item.is_enabled)
          .map((item) => ({
            label: item.name,
            value: item.id,
          })) || []
      );
    }
    return [];
  };

  return (
    <>
      <SACard>
        <SAStack
          pb={2}
          mb={1}
          alignItems="center"
          direction="row"
          gap={2}
          justifyContent="space-between"
          borderBottom="1px solid gray.main 50"
        >
          <SearchWrapper>
            <SATextField
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("search-for-materials")}
              InputProps={{ startAdornment: <Search /> }}
            />
          </SearchWrapper>
          <SAButton color="primary" variant="contained" onClick={openAddModal}>
            {t("add-material")}
          </SAButton>
        </SAStack>
        <SABasicTable
          {...{
            renderCell,
            paginationProps,
            data: materials,
            loading,
            columns: musharakahMaterialsColumns,
          }}
        />
      </SACard>

      <MUIModal
        open={modalOpen}
        maxWidth="xs"
        handleClose={() => setModalOpen(false)}
        title={t(
          modalMode === "add"
            ? "add-musharakah-material"
            : "edit-musharakah-material",
        )}
        content={
          <SABox
            py={4}
            px={{ xs: 2, md: 4 }}
            sx={{ backgroundColor: "#fff", borderRadius: 3, boxShadow: 1 }}
          >
            <SAForm
              key={modalDefaultValues?.id || modalMode}
              onSubmit={handleSubmit(onSubmit)}
            >
              <SAFormFields
                spacing={3}
                fields={musharakahMaterialsFields.map((field) => ({
                  ...field,
                  options: getOptions(field.name),
                }))}
                register={register}
                formState={formState}
                control={control}
                defaultValues={modalDefaultValues}
              />
              <SABox mt={4} textAlign="center">
                <SAButton
                  type="submit"
                  color="primary"
                  variant="contained"
                  loading={updateLoading}
                  disabled={modalMode === "edit" && !isDirty}
                >
                  {t(modalMode === "add" ? "add-material" : "save-changes")}
                </SAButton>
              </SABox>
            </SAForm>
          </SABox>
        }
      />
    </>
  );
};

export default MusharakahMaterials;
